// ─── Servicio unificado de presupuestos automáticos ──────────────────────────
// Todos los flujos que generan presupuestos automáticos (orden de laboratorio,
// orden de imagen, vacunas, antiparasitarios, derivación de cirugía, orden de
// hospitalización y agenda) deben usar crearPresupuestoAutomatico() para
// garantizar un esquema uniforme en la colección `presupuestos`:
//  - items con columna `prestacion` (no `descripcion`)
//  - descuentos del convenio del tutor aplicados por ítem
//  - orderNumber de la orden de origen registrado en el presupuesto

import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { siguienteNumeroPresupuesto } from './cajaService';
import { trackMetaEventServer } from './metaPixel';
import {
  netoDesdeBruto, ivaDesdeBruto, aplicarDescuentoPct,
  getArancelPrice, getConvenioDiscount, findConvenioByName,
} from '../utils/finance';
import { addDaysLocal } from '../utils/dateUtils';

const addDays = (n) => addDaysLocal(n);

// Resuelve el convenio activo del tutor. Se puede entregar directamente el
// nombre del convenio (convenioName) o el ownerId para leerlo desde `owners`.
export const resolverConvenio = async ({ ownerId = '', convenioName = '' } = {}) => {
  try {
    let name = convenioName;
    if (!name && ownerId) {
      const snap = await getDoc(doc(db, 'owners', ownerId));
      name = snap.exists() ? (snap.data().convenio || '') : '';
    }
    if (!name) return null;
    const convSnap = await getDocs(collection(db, 'convenios'));
    const convenios = convSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return findConvenioByName(convenios, name);
  } catch (e) {
    console.error('Error resolviendo convenio del tutor:', e);
    return null;
  }
};

/**
 * Crea un presupuesto automático con esquema unificado.
 *
 * @param {object} params
 * @param {string|null} params.orderId        ID del documento de la orden de origen.
 * @param {string} params.orderType           Tipo de orden: 'labOrder' | 'imagingOrder' | 'vacuna' | 'antiparasitario' | 'surgeryRequest' | 'hospitalizationOrder' | 'onlineBooking'.
 * @param {string} params.orderNumber         Número de la orden de origen (ej: LAB-20260807-1000).
 * @param {string} params.patientId
 * @param {string} params.patientName
 * @param {string} params.patientSpecies
 * @param {string} params.ownerId
 * @param {string} params.ownerName
 * @param {string} params.ownerPhone
 * @param {string} params.convenioName        Nombre del convenio del tutor (si ya se conoce; si no, se busca por ownerId).
 * @param {string} params.branchId
 * @param {string} params.vetId
 * @param {string} params.vetName
 * @param {Array}  params.items               [{ arancel, nombre, cantidad, precioFallback, tipoPrestacionFallback, notas }]
 *                                            - arancel: doc de `aranceles` (con id) o null
 *                                            - nombre: nombre de la prestación si no hay arancel
 *                                            - precioFallback: precio bruto si no hay arancel
 * @param {string} params.notas               Nota del presupuesto.
 * @param {string|null} params.updateOrderCollection  Colección de la orden para registrar presupuestoId/presupuestoNumber.
 * @returns {Promise<{id: string, numero: string}>}
 */
export const crearPresupuestoAutomatico = async ({
  orderId = null,
  orderType,
  orderNumber = '',
  patientId = '',
  patientName = '',
  patientSpecies = '',
  ownerId = '',
  ownerName = '',
  ownerPhone = '',
  convenioName = '',
  branchId = '',
  vetId = '',
  vetName = '',
  items = [],
  notas = '',
  updateOrderCollection = null,
}) => {
  const numero = await siguienteNumeroPresupuesto();
  const convenio = await resolverConvenio({ ownerId, convenioName });

  let descuentoTotal = 0;
  const itemsFinal = items.map((it) => {
    const arancel = it.arancel || null;
    const cantidad = Math.max(1, Number(it.cantidad) || 1);
    const precioUnitario = arancel
      ? getArancelPrice(arancel, branchId)
      : (Number(it.precioFallback) || 0);
    const tipoPrestacion = arancel?.tipoPrestacionName || arancel?.tipoPrestacion || it.tipoPrestacionFallback || '';
    const descPct = getConvenioDiscount(convenio, tipoPrestacion, arancel?.id);
    const sinDesc = precioUnitario * cantidad;
    const total = aplicarDescuentoPct(sinDesc, descPct);
    descuentoTotal += sinDesc - total;
    return {
      prestacion: arancel?.prestacion || it.nombre || '',
      descripcion: '',
      arancelId: arancel?.id || null,
      tipoPrestacion,
      unidadNegocio: arancel?.unidadNegocioName || '',
      cantidad,
      precioUnitario,
      descuento: sinDesc - total,
      total,
      notas: it.notas || '',
    };
  });

  const bruto = itemsFinal.reduce((sum, it) => sum + it.total, 0);
  const data = {
    numero,
    orderId,
    orderType,
    orderNumber: orderNumber || '',
    patientId,
    patientName,
    patientSpecies,
    ownerId,
    ownerName,
    ownerPhone,
    convenioId: convenio?.id || '',
    convenioName: convenio?.name || convenioName || '',
    branchId,
    vetId,
    vetName,
    items: itemsFinal,
    subtotal: netoDesdeBruto(bruto),
    descuentoTotal,
    iva: ivaDesdeBruto(bruto),
    total: bruto,
    validoHasta: addDays(30),
    estado: 'borrador',
    notas,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, 'presupuestos'), data);
  if (updateOrderCollection && orderId) {
    await updateDoc(doc(db, updateOrderCollection, orderId), {
      presupuestoId: ref.id,
      presupuestoNumber: numero,
    });
  }

  // Meta Pixel: evento AddToCart de Conversions API (sin exponer IP del usuario de la plataforma)
  trackMetaEventServer({
    eventName: 'AddToCart',
    value: data.subtotal,
    currency: 'CLP',
    owner: { name: ownerName, celular: ownerPhone, ownerId },
    itemName: orderType || 'Presupuesto Veterinario',
    contentIds: [numero],
    eventId: `addtocart_${numero}`,
  }).catch(() => {});

  return { id: ref.id, numero };
};
